import { ChangeEvent, useEffect, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { apiPaths } from '../../../../../service/apiPaths';
import ApiService from '../../../../../service/ApiService';
import { IAddTaskSheet } from '../../Tasksheet.props';

const useAddTaskSheet = () => {
  const today = new Date().toISOString().split("T")[0];

  const [formData, setFormData] = useState<IAddTaskSheet>({
    description: "",
    priority: "low",
    dueDate: today,
    status: "todo",
    projectId:"",
    videoUrl:"",
  });
  const [tasks, setTasks] = useState<IAddTaskSheet[]>(() => {
    const savedTasks = localStorage.getItem('tasks');
    return savedTasks ? JSON.parse(savedTasks) : [];
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [defaultPriority, setDefaultPriority] = useState<string>("low");
  const [defaultStatus, setDefaultStatus] = useState<string>("todo");
  const [project, setProject] = useState<any[]>([]);
  const [projectId, setProjectId] = useState<string>('');
  const [editIndex, setEditIndex] = useState<number | null>(null); 
  const navigate = useNavigate();

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { id, value, name } = e.target;
    setFormData({ ...formData, [id || name]: value });
  };

  useEffect(() => {
    fetchProjectData()
  }, []);

  const fetchProjectData = async () => {
    setLoading(true);
    try {
      const result = await ApiService({
        method: 'GET',
        endpoint: `${apiPaths.getProjects}`,
      });
      setProject(result);
      setProjectId(result[0].id);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };
  // Add or update a task
  const addOrUpdateTask = () => {
    if (tasks.length >= 10 && editIndex === null) {
      toast.error("You can only add a maximum of 10 tasks");
      return;
    }

    const updatedTask = { 
      ...formData, 
      priority: defaultPriority, 
      status: defaultStatus, 
      projectId: projectId,
      videoUrl: formData.videoUrl || '',
      isUserVideoReq: formData.isUserVideoReq || false
    };
    
    if (editIndex !== null) {
      const updatedTasks = tasks.map((task, index) =>
        index === editIndex ? updatedTask : task
      );
      setTasks(updatedTasks);
      setEditIndex(null);
    } else {
      setTasks([...tasks, updatedTask]);
    }

    setFormData({ 
      description: "", 
      priority: "low", 
      dueDate: today, 
      status: "todo", 
      projectId:'', 
      videoUrl:"",
      isUserVideoReq: false 
    });
  };

  const editTask = (index: number) => {
    const taskToEdit = tasks[index];
    setFormData(taskToEdit);
    setDefaultPriority(taskToEdit.priority ?? 'low');
    setDefaultStatus(taskToEdit.status ?? 'todo');
    setProjectId(taskToEdit.projectId ?? '');
    setEditIndex(index);
  };

  useEffect(() => {
    localStorage.setItem('tasks', JSON.stringify(tasks));
  }, [tasks]);

  const removeTask = (index: number) => {
    const updatedTasks = tasks.filter((_, i) => i !== index);
    setTasks(updatedTasks);
  };

  const handleSubmit: SubmitHandler<IAddTaskSheet> = async () => {
    if (tasks.length === 0) {
      toast.error("Please add at least one task.");
      return;
    }

    // Check if any task requires video and has no video URL
    const taskWithoutVideo = tasks.find(task => task.isUserVideoReq && !task.videoUrl);
    if (taskWithoutVideo) {
      toast.error("Please record a video for the task that requires it.");
      return;
    }
  
    setLoading(true);
    
    try {      
      for (const task of tasks) {
        await ApiService({
          method: "POST",
          endpoint: apiPaths.createEmpTaskSheet, 
          data: task, 
        });
      }
  
      toast.success("All tasks added successfully", { duration: 5000 });
      localStorage.removeItem('tasks'); 
      setTimeout(() => {
        navigate("/taskSheet"); 
      }, 1500);
    } catch (error) {
      toast.error(`Error - ${error}`, { duration: 5000 });
      console.error("Error adding tasks:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCancel = () => {
    navigate("/taskSheet");
  };

  const uploadVideo = () =>{
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'video/*';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        if (file.size > 200 * 1024 * 1024) {
          toast.error('File size should be less than 100MB');
          return;
        }
        if (!file.type.startsWith('video/')) {
          toast.error('Please upload a valid video file');
          return;
        }

        const formData = new FormData();
        formData.append('video', file);
        try {
          toast.loading('Uploading video...');
          const result = await ApiService({
            method: 'POST',
            endpoint: apiPaths.upload_video,
            data: formData,
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          toast.dismiss();
          if (result.videoUrl) {
            setFormData(prevData => ({ ...prevData, videoUrl: result.videoUrl }));
            toast.success('Video uploaded successfully');
          } else {
            toast.error('Failed to get video URL from server');
          }
        } catch (error) {
          toast.dismiss();
          console.error('Error uploading video:', error);
          toast.error('Failed to upload video. Please try again.');
        }
      }
    };
    input.click();
  }
 
  return {
    loading,
    formData,
    tasks,
    handleChange,
    handleSubmit,
    handleCancel,
    setFormData,
    setDefaultPriority,
    defaultPriority,
    defaultStatus,
    setDefaultStatus,
    project,
    projectId,
    setProjectId,
    addOrUpdateTask,
    removeTask,
    editTask,
    editIndex,
    uploadVideo
  };
};

export default useAddTaskSheet;
